import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  Grid,
  Stack,
  Typography,
  CardHeader,
  CardActionArea
} from "@mui/material";
import {
  EditOutlined as EditIcon,
  CreditCard as CreditCardIcon,
  GppBadRounded as DisabledIcon,
  GppGoodRounded as EnabledIcon
} from "@mui/icons-material";
import useSWR from "swr";

import { observer, useStore } from "../../../service/mobx";
import { fetcher, fetchFromApiServer } from "../../../service/graph";
import { Section } from "../.";
import ButtonFilled from "../../../component/Button/Filled";
import ToolTip from "../../../component/Tooltip";
import Skeleton from "../../../component/Skeleton";
import UpdateCard from "../../../component/Dialog/dialogs/api/UpdateCard";
import Credits from "../../../component/Dialog/dialogs/api/Credits";

function CreditsAndInvoice() {
  const [refetch, setRefetch] = useState(0);
  const { dialog, user, model } = useStore();
  const { isLoading, data } = useSWR(
    user.loggedIn ? `/api/stripe?view=card&t=${refetch}` : null,
    fetcher
  );
  const returnTo = useSearchParams().get("returnTo");
  const updateCard = useCallback(() => {
    dialog.configure({
      Content: () => <UpdateCard />,
      headline: "Update Payment Method",
      sx: {
        typography: {
          pb: 2,
          mx: "auto",
          component: "h2",
          fontWeight: "500 !important"
        },
        paper: {
          maxHeight: "unset",
          bgcolor: "var(--surface-container-highest)"
        }
      },
      onExited: () => {
        dialog.reset();
        setRefetch(refetch => refetch + 1);
      }
    });
  }, [dialog]);
  const card = data?.card;

  return (
    <Section sx={{ maxWidth: 840, m: "auto", mt: 4, p: 2, width: "100%" }}>
      <Stack>
        <Typography variant="titleMd" fontWeight={600}>
          Credits
        </Typography>
        <Typography variant="bodyMd" color="var(--secondary-color)">
          Your credits are consumed when you run models
        </Typography>
      </Stack>
      <Stack spacing={3} borderRadius="var(--shape-lg-round)">
        <Grid container columns={10} spacing={4}>
          {/* Left Column: Balance Card */}
          <Grid size={{ compact: 10, expanded: 4 }}>
            <Balance />
          </Grid>
          {/* Right Column: Actions */}
          <Grid size={{ compact: 10, expanded: 6 }}>
            <Stack
              spacing={3}
              height="100%"
              justifyContent={model.premiumPlan ? "center" : undefined}
            >
              <Typography
                variant="bodyMd"
                align="center"
                color="var(--secondary-color)"
              >
                {model.premiumPlan
                  ? "You can buy credits directly or set auto-reload thresholds."
                  : user.loggedIn
                  ? "Free credits refresh monthly. If you can't wait, switch to pay-as-you-go"
                  : "Sign in to see your credits"}
              </Typography>
              {model.premiumPlan ? (
                <>
                  {/* Card Details */}
                  <ChangeCard
                    card={card}
                    updateCard={updateCard}
                    setRefetch={setRefetch}
                    isLoading={isLoading}
                  />
                  {/* Auto Reload Box */}
                  <AutoReload card={card} updateCard={updateCard} />
                </>
              ) : (
                <ButtonFilled
                  fullWidth
                  size="large"
                  href={
                    user.loggedIn
                      ? "/api/pricing"
                      : `/auth?returnTo=${encodeURIComponent(
                          `/api/pricing${
                            returnTo ? `?returnTo=${returnTo}` : ""
                          }`
                        )}`
                  }
                  sx={{ borderRadius: "var(--shape-sm)" }}
                  label={user.loggedIn ? "Upgrade To Pay-As-You-Go" : "Sign in"}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Section>
  );
}
export default observer(CreditsAndInvoice);

const ChangeCard = observer(function ChangeCard({
  isLoading,
  card,
  setRefetch,
  updateCard
}) {
  const { user, dialog } = useStore();
  const returnTo = useSearchParams().get("returnTo");

  return (
    <Stack
      p={2}
      spacing={1}
      borderRadius="var(--shape-md-round)"
      bgcolor="var(--surface-container-high)"
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton height={40} width={250} />
        ) : (
          <ToolTip title="Edit your payment method">
            <CardActionArea
              href={
                user.isAnonymous
                  ? `/auth?returnTo=${encodeURIComponent(
                      `/api/pricing${returnTo ? `?returnTo=${returnTo}` : ""}`
                    )}`
                  : undefined
              }
              sx={{
                transition: "all 200ms",
                width: "fit-content",
                borderRadius: "var(--shape-sm)",
                bgcolor: card
                  ? "var(--surface-container-color)"
                  : "var(--tertiary-container)",
                "&:hover": {
                  backgroundColor: card
                    ? "var(--primary-container)"
                    : "var(--tertiary-container)"
                }
              }}
              onClick={user.isAnonymous ? undefined : updateCard}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                px={1.5}
                py={1}
              >
                <CreditCardIcon
                  fontSize="small"
                  sx={{ color: "var(--secondary-color)", opacity: 0.8 }}
                />
                <Typography
                  fontWeight={500}
                  color={card ? undefined : "var(--tertiary-on-container)"}
                  textTransform={card ? "capitalize" : undefined}
                >
                  {card
                    ? `${card.brand} •••• ${card.last4}`
                    : "Add payment method"}
                </Typography>
                <EditIcon fontSize="inherit" />
              </Stack>
            </CardActionArea>
          </ToolTip>
        )}
        <ButtonFilled
          label="Buy Credits"
          disabled={isLoading || card === undefined}
          sx={{ borderRadius: "var(--shape-sm)" }}
          onClick={() => {
            dialog.configure({
              Content: () => <Credits buyCredits />,
              headline: "Add Credits",
              sx: {
                firstStack: { flex: 1 },
                typography: {
                  fontWeight: "500 !important",
                  pb: { compact: 0, expanded: 2 },
                  mx: { compact: "unset", expanded: "auto" }
                },
                paper: {
                  maxHeight: "unset",
                  bgcolor: "var(--surface-container-highest)"
                }
              },
              onExited: () => {
                dialog.reset();
                setRefetch(refetch => refetch + 1);
              }
            });
          }}
        />
      </Stack>
    </Stack>
  );
});
const AutoReload = observer(function AutoReload({ card, updateCard }) {
  const { dialog, user } = useStore();
  const returnTo = useSearchParams().get("returnTo");
  const {
    enabled = false,
    when = 0,
    to = 0,
    limit = 0
  } = user.profile.billing?.reload ?? {};

  return (
    <CardHeader
      title={`Auto-reload is ${enabled ? "enabled" : "disabled"}`}
      subheader={
        enabled
          ? `Refills to $${to} at $${when}${
              limit ? `. Max $${limit} / month.` : ""
            }`
          : "Enable to avoid service disruptions when credits are low"
      }
      avatar={
        enabled ? (
          <EnabledIcon color="success" />
        ) : (
          <DisabledIcon sx={{ color: "var(--tertiary-color)" }} />
        )
      }
      slotProps={{
        title: {
          variant: "titleSm",
          color: enabled ? "success.main" : "var(--tertiary-color)"
        },
        subheader: {
          variant: "bodySm",
          sx: { textWrap: "balance" }
        }
      }}
      action={
        <ButtonFilled
          label={enabled ? "Edit" : "Enable"}
          sx={{ borderRadius: "var(--shape-sm)" }}
          href={
            user.isAnonymous
              ? `/auth?returnTo=${encodeURIComponent(
                  `/api/pricing${returnTo ? `?returnTo=${returnTo}` : ""}`
                )}`
              : undefined
          }
          onClick={
            user.isAnonymous
              ? undefined
              : () => {
                  if (card) {
                    dialog.configure({
                      Content: () => <Credits editAutoReload />,
                      headline: "Update Auto-Reload",
                      sx: {
                        typography: {
                          fontWeight: "500 !important",
                          pb: { compact: 0, expanded: 2 },
                          mx: { compact: "unset", expanded: "auto" }
                        },
                        paper: {
                          maxHeight: "unset",
                          bgcolor: "var(--surface-container-highest)"
                        }
                      }
                    });
                  } else {
                    updateCard();
                  }
                }
          }
        />
      }
      sx={{
        alignItems: "flex-start",
        bgcolor: "var(--surface-container-high)",
        borderColor: "var(--outline-variant)",
        borderRadius: "var(--shape-md-round)",
        "& .MuiCardHeader-action": {
          m: 0,
          alignSelf: "center"
        }
      }}
    />
  );
});

const Balance = observer(function Balance() {
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    async function getBalance() {
      const { balance } = await fetchFromApiServer({
        path: "billing/balance"
      });

      setBalance(balance);
    }
    getBalance();
  }, []);

  return (
    <Stack
      p={5}
      justifyContent="center"
      alignItems="center"
      bgcolor="var(--secondary-color)"
      borderRadius="var(--shape-sm)"
      height="100%"
    >
      <Stack
        spacing={0.5}
        alignContent="center"
        direction="row"
        justifyContent="center"
      >
        <Typography
          variant="headlineSm"
          fontWeight={600}
          color="var(--secondary-on-color)"
        >
          $
        </Typography>
        <Typography
          fontWeight={600}
          variant="displayMd"
          color="var(--secondary-on-color)"
        >
          {Number(Math.floor(balance * 100) / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}
        </Typography>
      </Stack>
      <Typography color="var(--secondary-on-color)">
        Remaining Balance
      </Typography>
    </Stack>
  );
});
